import type { Selection } from '../urlState'

/**
 * AN UNSENT MESSAGE BELONGS TO THE CONVERSATION IT WAS TYPED IN — owner, 2026-08-22:
 * 讓 Text input 隨著 Agent / Project chat / Friend chat 的 Session. 文字輸入到一半的
 * Text edit 不會跑到其他的 Session 而是就地有記憶。之後可以回來繼續完成內容.
 *
 * The composer is one textarea shared by every destination, so a half-written line
 * followed the person to whoever they opened next — either sent to the wrong place or
 * cleared by hand, and in both cases the thinking was lost.
 *
 * KEYED BY THE STABLE HALF OF THE SELECTION. An agent opened from a hash URL arrives
 * with `session`/`host` null and fills them in once the agent list resolves; keying on
 * the name would move the draft mid-edit, so the hash wins where there is one. A DM
 * carries `peer` in-app and an opaque `key` in a shared link, and either identifies the
 * same conversation.
 *
 * ONE ENTRY, not one localStorage key per conversation: this app can see dozens of
 * agents, and a key per conversation is an unbounded set nobody prunes. The map is
 * capped both ways — how many drafts, and how long each — because a draft is a
 * convenience and must never be the reason storage fills up.
 *
 * Storage is per-viewer and best-effort by design. Every read and write is wrapped:
 * a private window, cleared site data, or a browser set to block storage all throw on
 * access, and a lost draft is a smaller failure than a composer that cannot open.
 */

const STORE = 'wt_drafts'
/**
 * RAISING EITHER OF THESE BRINGS BACK A COST THAT IS CURRENTLY FINE.
 *
 * Saving rewrites the WHOLE map, so the write is O(everything stored) and it sits on
 * the typing path. avg-latency measured it at the caps below rather than reasoning
 * about it (2026-08-22):
 *
 *   1 draft × 200 chars   0KB     ~0ms per save
 *   10 × 2k              20KB     ~0.1ms
 *   40 × 8k  (the caps) 314KB     ~1ms          — and roughly 3–5× that on a phone
 *
 * A slope, not a floor, and the slope's far end is 1ms: acceptable, and not worth
 * restructuring for. What makes it acceptable is THESE NUMBERS, so if you raise a cap,
 * re-measure — or split to one localStorage key per conversation plus a small index,
 * which removes the slope entirely and was deliberately not built today because the
 * reading did not call for it.
 *
 * Still unmeasured, and unmeasurable from a headless box: `setItem` itself is
 * synchronous and reaches real storage on a phone, which may cost more than the
 * serialising does.
 */
/** Long enough for anything a person types into a chat box; short of a pasted file. */
const MAX_CHARS = 8_000
/** Oldest are dropped first — see `writeDraft`. */
const MAX_DRAFTS = 40

/** A stable id for the conversation the composer is currently pointed at. */
export function draftKey(sel: Selection): string | null {
  if (!sel) return null
  if (sel.kind === 'project') return sel.id ? `project:${sel.id}` : null
  if (sel.kind === 'shared') return `shared:${sel.id}`
  if (sel.kind === 'dm') {
    const who = sel.peer || sel.key
    return who ? `dm:${who}` : null
  }
  if (sel.kind === 'agent') {
    /*
     * Hash first, and `device` with it: a LOCAL agent hashes host="", so one name on
     * two machines produces one hash — the same collision the Selection type warns
     * about. Falling back to session+host keeps older links working.
     */
    if (sel.hash) return `agent:${sel.hash}:${sel.device ?? ''}`
    return sel.session ? `agent:${sel.session}@${sel.host ?? ''}` : null
  }
  return null
}

type Store = Record<string, { text: string; at: number }>

/**
 * PARSED ONCE, not on every keystroke.
 *
 * The typing path saves on a throttle, and a throttle is not a licence to be slow: at
 * the caps above the stored map can reach a few hundred KB, and JSON.parse-ing that
 * two or three times a second on a phone is exactly the kind of cost that shows up as
 * the composer feeling heavy. The whole reason this box is uncontrolled is that the
 * owner felt a per-keystroke cost once already (輸入超卡); adding one back inside the
 * feature that was supposed to help would be the same mistake wearing a different hat.
 *
 * The cache is this tab's own. Nothing else writes the key, and a second tab's drafts
 * being one write behind is not worth a storage event listener.
 */
let cache: Store | null = null

function load(): Store {
  if (cache) return cache
  try {
    const raw = window.localStorage.getItem(STORE)
    const parsed: unknown = raw ? JSON.parse(raw) : null
    cache = parsed && typeof parsed === 'object' ? (parsed as Store) : {}
  } catch {
    cache = {}
  }
  return cache
}

/** Tests only: forget what this module has cached, as a fresh page would. */
export function __resetDraftCache(): void {
  cache = null
}

/** What was left unsent here, or '' — never null, so a caller can write it straight in. */
export function readDraft(key: string | null): string {
  if (!key) return ''
  const hit = load()[key]
  return hit && typeof hit.text === 'string' ? hit.text : ''
}

/**
 * Remember (or, for empty text, forget) what is in the box for this conversation.
 *
 * Empty DELETES rather than storing an empty string: a sent message clears the
 * composer, and a stored blank would otherwise sit there forever taking a slot from a
 * draft somebody actually wants.
 */
export function writeDraft(key: string | null, text: string, now = Date.now()): void {
  if (!key) return
  try {
    const store = load()
    if (!text) delete store[key]
    else store[key] = { text: text.slice(0, MAX_CHARS), at: now }
    const keys = Object.keys(store)
    if (keys.length > MAX_DRAFTS) {
      // Oldest first: the conversation somebody is still thinking about is the one
      // they touched most recently.
      const byAge = keys.sort((a, b) => (store[a]?.at ?? 0) - (store[b]?.at ?? 0))
      for (const k of byAge.slice(0, keys.length - MAX_DRAFTS)) delete store[k]
    }
    cache = store
    window.localStorage.setItem(STORE, JSON.stringify(store))
  } catch {
    /* storage unavailable or full — a lost draft must not break the composer */
  }
}
